<template>
  <div :class="promotionSelectionSectionClasses">
    <PromotionElementSelect
      v-for="selectConfig in expandedSelectConfigs"
      :key="selectConfig.key"
      :group="selectConfig.group"
      :variants="variants"
      :selected-group-variants="selectedGroupVariants"
      :selection-index="selectConfig.index"
      :error="!isSelectionValid(selectConfig.group.id, selectConfig.index)"
      :label="
        $t('purchaseV2.basket.addElement', {
          name: selectConfig.group.name,
        }).toUpperCase()
      "
      @input="
        updateGroupSelection(selectConfig.group.id, selectConfig.index, $event)
      " />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import PromotionElementSelect from './PromotionElementSelect.vue'
import { COIN_TYPES } from '@/constants/coins'

export default {
  name: 'BasketItemPromotionElementSelectSection',
  components: {
    PromotionElementSelect,
  },
  props: {
    promotion: { type: Object, default: () => ({}) },
    variants: { type: Array, default: () => [] },
    cartItem: { type: Object, default: null },
  },
  data: () => ({
    selectedGroupVariants: {},
  }),
  computed: {
    ...mapGetters('cart', {
      isPromotionSelectionComplete: 'isPromotionSelectionComplete',
    }),
    promotionSelectionSectionClasses() {
      const validationStateClass = this.isSelectionComplete
        ? 'basket-item__promotion--valid'
        : 'basket-item__promotion--error'
      return `basket-item__promotion-grid ${validationStateClass}`
    },
    promotionGroups() {
      return Array.isArray(this.promotion?.groups) ? this.promotion.groups : []
    },
    isSelectionComplete() {
      return this.isPromotionSelectionComplete(this.promotion.id)
    },
    expandedSelectConfigs() {
      const configs = []
      this.promotionGroups.forEach((group) => {
        const isCoinsGroup = this.isCoinsGroup(group)
        // For coins groups, always use single select (ignore quantity)
        // For other groups, create selects based on quantity
        const quantity = isCoinsGroup ? 1 : Number(group.quantity) || 1
        for (let i = 0; i < quantity; i++) {
          configs.push({
            key: `${group.id}-${i}`,
            group,
            index: i,
          })
        }
      })
      return configs
    },
  },
  watch: {
    promotionGroups: {
      handler() {
        this.syncGroupSelections()
      },
      immediate: true,
    },
    promotion: {
      handler() {
        this.syncGroupSelections()
      },
      deep: true,
    },
    cartItem: {
      handler(newItem) {
        // Initialize from cart item's groupSelections if available
        if (newItem?.groupSelections) {
          this.selectedGroupVariants = { ...newItem.groupSelections }
        }
        this.syncGroupSelections()
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    syncGroupSelections() {
      if (!this.promotionGroups.length) {
        this.selectedGroupVariants = {}
        return
      }

      const existingSelections = this.cartItem?.groupSelections || {}

      this.selectedGroupVariants = this.promotionGroups.reduce(
        (nextSelections, group) => {
          const quantity = Number(group.quantity) || 1
          const existingArray = existingSelections[group.id] || []
          const currentArray = this.selectedGroupVariants[group.id] || []

          // Use existing selection if available, otherwise use current
          const baseArray = existingArray.length ? existingArray : currentArray

          // Normalize array length to match quantity
          const padding = quantity - baseArray.length
          let normalizedArray
          if (padding > 0) {
            normalizedArray = [...baseArray, ...Array(padding).fill(null)]
          } else if (padding < 0) {
            normalizedArray = baseArray.slice(0, quantity)
          } else {
            normalizedArray = baseArray
          }

          nextSelections[group.id] = normalizedArray
          return nextSelections
        },
        {},
      )
    },
    updateGroupSelection(groupId, index, value) {
      const group = this.promotionGroups.find((g) => g.id === groupId)
      const quantity = Number(group?.quantity) || 1

      const currentArray = Array.isArray(this.selectedGroupVariants[groupId])
        ? [...this.selectedGroupVariants[groupId]]
        : []

      while (currentArray.length < quantity) {
        currentArray.push(null)
      }

      currentArray[index] = value || null

      const nextSelections = {
        ...this.selectedGroupVariants,
        [groupId]: currentArray,
      }

      this.selectedGroupVariants = nextSelections
      this.$store.dispatch('cart/setPromotionSelection', {
        promotion: this.promotion,
        selections: nextSelections,
      })
    },
    isSelectionValid(groupId, index) {
      const selections = this.selectedGroupVariants[groupId]
      return Array.isArray(selections) && !!selections[index]
    },
    isCoinsGroup(group) {
      if (!group?.variants?.length || !this.variants.length) return false

      const groupVariantIds = new Set(
        group.variants.map((gv) => gv.arena_variant_id),
      )

      return this.variants.some(
        (variant) =>
          groupVariantIds.has(variant.id) &&
          variant?.attributes?.type === COIN_TYPES.coins,
      )
    },
  },
}
</script>

<style scoped lang="scss">
.basket-item__promotion-grid {
  display: grid;
  gap: 0.625rem;
  grid-template-columns: repeat(3, 1fr);
  border-radius: 12px;
  border: 1px solid transparent;
  padding: 0.75rem;
}

.basket-item__promotion-grid > * {
  min-width: 0;
  width: 100%;
}

.basket-item__promotion-grid > :nth-child(4n) {
  grid-column: 1 / -1;
}

.basket-item__promotion--error {
  border-color: #d74a4a;
  background-color: rgba(215, 74, 74, 0.12);
}

.basket-item__promotion--valid {
  border-color: rgba(56, 111, 247, 0.4);
  background-color: rgba(56, 111, 247, 0.08);
}

@media (min-width: 1200px) {
  .basket-item__promotion-grid {
    max-width: 100%;
  }
}
</style>
